import PhoneCallbackOutlinedIcon from "@mui/icons-material/PhoneCallbackOutlined";
import { Alert, Box, Button, Typography } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import { differenceInHours, differenceInMinutes, isPast } from "date-fns";
import { format } from "date-fns";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useDateFnsLocale } from "../../../../utils/date-locale";
import useWorkerRequestsData from "../../controllers/use-worker-requests-data";
import CallDetailDrawer from "./call-detail-drawer";

interface WorkerRequestDetailProps {
	handleShiftCoverage: ((value: boolean) => void) | undefined;
	isShiftCoverage: boolean | undefined;
}

const WorkerRequestDetail = ({
	handleShiftCoverage,
	isShiftCoverage,
}: WorkerRequestDetailProps) => {
	const { t } = useTranslation();
	const dateLocale = useDateFnsLocale();
	const { workerRequestMetaData, attendanceManagement, callOffLineEnabledLocations } =
		useWorkerRequestsData();
	const [isCallDetailOpen, setIsCallDetailOpen] = useState(false);

	const name = workerRequestMetaData?.name;
	const requestType = workerRequestMetaData?.requestType;
	const requestCode = workerRequestMetaData?.requestCode;
	const leaveType = attendanceManagement
		? ` ${workerRequestMetaData?.leaveType} `
		: " ";
	const startDateTime = workerRequestMetaData?.startDateTime;
	const endDateTime = workerRequestMetaData?.endDateTime;
	const workerComment = workerRequestMetaData?.workerComment;
	const shiftDetails = workerRequestMetaData?.shiftDetails;
	const requestStatusCode = workerRequestMetaData?.requestStatusCode;
	const requestId = workerRequestMetaData?.requestId;
	const conversationIds: string[] =
		workerRequestMetaData?.conversationIds ?? [];
	// Recording/transcript access requires call-off line to be enabled for a
	// location this user can access; an empty list means no access at all.
	const isCallOffLineEnabled = (callOffLineEnabledLocations?.length ?? 0) > 0;
	const showCallDetail =
		isCallOffLineEnabled &&
		Boolean(workerRequestMetaData?.callOffLineEnabled) &&
		conversationIds.length > 0 &&
		requestId != null;

	const date = format(new Date(startDateTime), "d LLL yyyy", {
		locale: dateLocale,
	});
	const endTime = format(new Date(endDateTime), "h.mm a");
	const startTime = format(new Date(startDateTime), "h.mm a");

	const shiftDuration = differenceInMinutes(
		new Date(endDateTime),
		new Date(startDateTime)
	);
	const hours = Math.floor(shiftDuration / 60);
	const minutes = shiftDuration % 60;
	const shiftDurationString =
		minutes > 0
			? t("worker-requests_duration-hm", { hours, minutes })
			: t("worker-requests_duration-hours", { hours });
	const isSwapRequest = requestCode === "ss";

	useEffect(() => {
		if (isPast(new Date(startDateTime))) {
			handleShiftCoverage && handleShiftCoverage(false);
		}
	}
	, [handleShiftCoverage, startDateTime]);

	return (
    <Box
      width="100%"
      padding={"12px 0"}
      sx={{
        backgroundColor: "#fff",
      }}
    >
      <Alert severity="info">
        {t("worker-requests_request-summary", {
          name,
          requestType,
          duration: shiftDurationString,
          shiftDetails,
          date,
          startTime,
          endTime,
        })}
        <br />
        {t("worker-requests_leave-type-used", { leaveType })}
      </Alert>
      <Box mt={3} mb={3}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "21px",
              color: "#000000",
            }}
          >
            {t("worker-requests_worker-reason")}
          </Typography>
          {showCallDetail ? (
            <Button
              variant="text"
              size="small"
              startIcon={<PhoneCallbackOutlinedIcon sx={{ fontSize: "16px" }} />}
              onClick={() => setIsCallDetailOpen(true)}
              sx={{
                textTransform: "none",
                fontSize: "13px",
                fontWeight: 600,
                color: "#2F4D8B",
                padding: "2px 6px",
                flex: "0 0 auto",
              }}
            >
              {t("worker-requests_listen-and-read-transcript")}
            </Button>
          ) : null}
        </Box>
        <Typography
          sx={{
            fontFamily: "Roboto",
            fontWeight: 400,
            fontSize: "14px",
            lineHeight: "21px",
            color: "#000000",
          }}
        >
          {!workerComment ? "-" : workerComment}
        </Typography>
      </Box>
      {showCallDetail ? (
        <CallDetailDrawer
          open={isCallDetailOpen}
          requestId={requestId}
          conversationIds={conversationIds}
          workerName={name ?? t("worker-requests_worker")}
          requestDate={workerRequestMetaData?.requestDate}
          onClose={() => setIsCallDetailOpen(false)}
        />
      ) : null}
      <Typography
        sx={{
          fontFamily: "Roboto",
          fontWeight: 500,
          fontSize: "14px",
          lineHeight: "21px",
          color: isSwapRequest ? "#00000061" : "#000000",
        }}
      >
        {t("worker-requests_get-coverage-question")}
      </Typography>
      {isSwapRequest ? (
        <Typography
          sx={{
            fontFamily: "Roboto",
            fontWeight: 500,
            fontSize: "12px",
            lineHeight: "21px",
            color: " #00000099",
          }}
        >
          {t("worker-requests_swap-always-coverage")}
        </Typography>
      ) : null}
      <FormControl>
        <RadioGroup
          aria-labelledby="demo-controlled-radio-buttons-group"
          name="controlled-radio-buttons-group"
          value={isShiftCoverage ? "yes" : "no"}
          onChange={() =>
            handleShiftCoverage && handleShiftCoverage(!isShiftCoverage)
          }
          sx={{ display: "flex", flexDirection: "row" }}
        >
          <FormControlLabel
            value="yes"
            control={<Radio />}
            label={t("worker-requests_yes")}
            disabled={isSwapRequest || isPast(new Date(startDateTime))}
          />
          <FormControlLabel
            value="no"
            control={<Radio />}
            label={t("worker-requests_no")}
            disabled={isSwapRequest}
          />
        </RadioGroup>
      </FormControl>
    </Box>
  );
};

export default WorkerRequestDetail;
